import { useEffect } from "react";
import { Scan, LockOpen, VolumeX, Wrench, CreditCard, Banknote, MapPin, Mail, Linkedin } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";


import casestudy2 from "@/assets/deck/casestudy-2.jpg";
import casestudy3 from "@/assets/deck/casestudy-3.jpg";
import casestudy4 from "@/assets/deck/casestudy-4.jpg";
import podInSitu from "@/assets/deck/pod-in-situ.jpeg";
import tflLogo from "@/assets/deck/tfl-logo.png";
import slide1Bg from "@/assets/deck/slide1-bg.jpg";
import qwipFounders from "@/assets/deck/qwip-founders.jpg";

const kgxTestimonials = [
  { quote: "I had to catch my Eurostar train in an hour, and had nowhere private to take an important client call. I was so close to cancelling that meeting and this was exactly what I needed in the moment.", author: "Adnaan, Consultant" },
  { quote: "I need this EVERY DAY. I work from cafes, hotel lobbies, wherever I can find a seat, but I always struggle with the noise.", author: "Rachael, 24, Law Student" },
  { quote: "I hope these pop up everywhere. As a parent who works remotely, this would make my life so much easier.", author: "Mark, 39, Brand Agency Owner" },
];

const Slide = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`slide-page w-full aspect-video relative overflow-hidden ${className}`} style={{ pageBreakAfter: "always", breakAfter: "page" }}>
    {children}
  </div>
);

const DeckPrint = () => {
  useEffect(() => {
    // Small delay to let images load, then trigger print
    const timer = setTimeout(() => window.print(), 1500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <style>{`
        @media print {
          @page { size: landscape; margin: 0; }
          body { margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .no-print { display: none !important; }
          .slide-page { page-break-after: always; break-after: page; height: 100vh; width: 100vw; }
          .slide-page:last-child { page-break-after: avoid; }
        }
        @media screen {
          .slide-page { margin-bottom: 2px; }
        }
      `}</style>

      {/* Print button - screen only */}
      <div className="no-print fixed top-4 right-4 z-50 flex gap-2">
        <button
          onClick={() => window.print()}
          className="bg-foreground text-primary-foreground px-4 py-2 rounded-lg font-sans text-sm font-medium hover:opacity-90"
        >
          Save as PDF
        </button>
        <button
          onClick={() => window.close()}
          className="bg-foreground/10 text-foreground px-4 py-2 rounded-lg font-sans text-sm font-medium hover:bg-foreground/20"
        >
          Close
        </button>
      </div>

      {/* Slide 1: Intro */}
      <Slide>
        <div
          className="flex flex-col items-center justify-center h-full text-center px-24 relative"
          style={{ backgroundImage: `url(${slide1Bg})`, backgroundSize: "cover", backgroundPosition: "center" }}
        >
          <div className="absolute inset-0 bg-black/20" />
          <h1 className="font-funnel font-bold text-9xl text-white leading-[0.9] tracking-tight mb-8 relative z-10">qwip</h1>
          <p className="text-2xl text-white/80 max-w-2xl font-funnel leading-relaxed relative z-10">the public phone booth, upgraded.</p>
        </div>
      </Slide>

      {/* Slide 2: Who We Are */}
      <Slide>
        <div className="grid grid-cols-2 h-full">
          <div className="flex flex-col justify-center px-16 py-12">
            <p className="font-funnel font-bold text-2xl text-primary mb-4">qwip</p>
            <h2 className="font-funnel text-5xl text-foreground leading-[0.95] tracking-tight mb-8">
              We place <span className="font-bold">private, soundproof pods</span> in <span className="font-bold">public spaces</span>.
            </h2>
            <p className="text-lg text-muted-foreground font-funnel leading-relaxed max-w-lg mb-2">
              your visitors get a private place to take <strong>calls</strong>, <strong>work</strong>, or have a moment of <strong>quiet</strong> - without having to leave your space.
            </p>
            <p className="text-lg text-muted-foreground font-funnel leading-relaxed max-w-lg">You get paid every time they book.</p>
            <div className="mt-10 grid grid-cols-3 gap-4 max-w-lg">
              {["zero upfront cost", "up to £3,500/month revenue", "increase retention and stand out"].map((text) => (
                <div key={text} className="border border-foreground/15 rounded-2xl p-5 flex items-center justify-center text-center">
                  <p className="font-funnel text-base font-bold leading-snug text-foreground">{text}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-center p-8">
            <img src={podInSitu} alt="qwip pod" className="w-full h-full object-cover rounded-3xl max-h-[80vh]" />
          </div>
        </div>
      </Slide>

      {/* Slide 3: King's Cross */}
      <Slide>
        <div className="grid grid-cols-2 h-full">
          <div className="flex flex-col justify-center px-16 py-6">
            <div className="flex items-center gap-6 mb-3">
              <span className="font-funnel font-bold text-5xl text-foreground">qwip</span>
              <div className="w-px h-14 bg-foreground/20" />
              <img src={tflLogo} alt="TfL" className="h-16 object-contain" />
            </div>
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="w-4 h-4 text-muted-foreground" />
              <span className="font-sans text-sm text-muted-foreground font-medium">King's Cross Station</span>
            </div>
            <p className="text-base text-muted-foreground font-funnel leading-relaxed max-w-lg mb-4">
              this january, we trialled our first pod at King's Cross Station - the busiest transport hub in the UK.
              <br /><br />the results spoke for themselves:
            </p>
            <div className="flex gap-3 mb-4 max-w-lg">
              {[
                { value: ">£100", label: "revenue in a single day" },
                { value: "~60%", label: "utilisation rate" },
              ].map((item) => (
                <div key={item.label} className="flex-1 bg-foreground text-primary-foreground rounded-2xl p-4">
                  <p className="font-funnel font-bold text-2xl mb-1">{item.value}</p>
                  <p className="text-primary-foreground/70 font-sans text-xs">{item.label}</p>
                </div>
              ))}
            </div>
            <p className="font-funnel font-bold text-xl text-foreground mb-3">londoners want qwip.</p>
            <div className="bg-foreground/5 border border-foreground/10 rounded-2xl p-5">
              <p className="font-sans text-foreground text-base leading-relaxed mb-3">"{kgxTestimonials[0].quote}"</p>
              <p className="font-sans text-xs text-muted-foreground font-semibold uppercase tracking-wider">{kgxTestimonials[0].author}</p>
            </div>
          </div>
          <div className="flex items-center justify-center p-6">
            <img src={qwipFounders} alt="qwip founders" className="w-full h-full object-cover rounded-3xl" />
          </div>
        </div>
      </Slide>

      {/* Slide 4: Case Study */}
      <Slide className="bg-primary">
        <div className="flex flex-col h-full px-16 py-12 justify-center">
          <div className="absolute top-6 right-6 font-funnel font-bold text-xl text-white">qwip</div>
          <h2 className="font-funnel font-bold text-5xl text-white leading-[0.95] tracking-tight mb-12">
            other cities upgraded the phone booth.<br />
            <span className="font-cormorant italic font-normal">we're bringing it home.</span>
          </h2>
          <div className="grid grid-cols-3 gap-8">
            {[
              { img: casestudy2, country: "Japan", stat: "400+", description: "booths deployed across Japanese railway stations" },
              { img: casestudy3, country: "United States", stat: "50+", description: "booths across hotel lobbies and airports" },
              { img: casestudy4, country: "Middle East", stat: "30+", description: "booths across shopping centres, cafés, and fitness clubs" },
            ].map((item) => (
              <div key={item.country}>
                <div className="w-full aspect-[4/3] rounded-2xl overflow-hidden mb-3">
                  <img src={item.img} alt={item.country} className="w-full h-full object-cover" />
                </div>
                <div className="flex items-center gap-2 mb-1">
                  <MapPin className="w-4 h-4 text-white/60" />
                  <p className="font-funnel font-bold text-white text-lg">{item.country}</p>
                </div>
                <p className="text-white/70 text-sm font-funnel leading-relaxed">{item.stat} {item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </Slide>

      {/* Slide 5: How It Works */}
      <Slide>
        <div className="flex flex-col h-full">
          <div className="text-center pt-8 pb-4">
            <h2 className="font-funnel font-bold text-4xl text-foreground tracking-tight">how it works</h2>
          </div>
          <div className="flex-1 flex items-center px-14">
            <div className="w-full grid grid-cols-[1fr_1px_1fr] gap-x-12">
              <div className="mb-4">
                <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground font-sans font-bold">for the user</p>
              </div>
              <div />
              <div className="mb-4">
                <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground font-sans font-bold">for the host</p>
              </div>
              {[
                { icon: Scan, title: "scan", desc: "user scans the QR code on the pod to start the booking process." },
                { icon: LockOpen, title: "unlock", desc: "they get an access code, enter it on the numpad, and unlock the pod." },
                { icon: VolumeX, title: "focus", desc: "the pod is all theirs. they're billed by the minute and charged at the end." },
              ].map((step, i) => {
                const hostSteps = [
                  { icon: Wrench, title: "we install", desc: "we deliver and install the pod (1m x 1m) in your space - zero cost, zero hassle." },
                  { icon: CreditCard, title: "users pay", desc: "visitors scan, book, and pay by the minute. all payments handled by us." },
                  { icon: Banknote, title: "you earn", desc: "we operate on a revenue share, or fixed rent model." },
                ];
                const host = hostSteps[i];
                return (
                  <div key={step.title} className="contents">
                    <div className="flex gap-4 items-start p-3">
                      <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-foreground text-primary-foreground flex items-center justify-center">
                        <step.icon className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-funnel font-bold text-lg text-foreground mb-1">{step.title}</p>
                        <p className="text-muted-foreground text-sm font-sans leading-relaxed">{step.desc}</p>
                      </div>
                    </div>
                    <div className="bg-foreground/10" />
                    <div className="flex gap-4 items-start p-3">
                      <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-foreground text-primary-foreground flex items-center justify-center">
                        <host.icon className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-funnel font-bold text-lg text-foreground mb-1">{host.title}</p>
                        <p className="text-muted-foreground text-sm font-sans leading-relaxed">{host.desc}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Slide>

      {/* Slide 6: Revenue */}
      <Slide className="bg-primary">
        <div className="flex flex-col items-center justify-center h-full text-center px-16">
          <div className="absolute top-6 right-6 font-funnel font-bold text-xl text-white">qwip</div>
          <h2 className="font-funnel font-bold text-5xl text-white leading-[0.95] tracking-tight mb-4">
            your space makes money<br />
            <span className="font-cormorant italic font-normal">whilst you sleep.</span>
          </h2>
          <p className="text-lg text-white/70 font-sans mb-4">it costs you <span className="font-bold text-white">absolutely nothing.</span></p>
          <div className="w-16 h-px bg-white/20 mb-4" />
          <p className="text-sm uppercase tracking-[0.3em] text-white/50 font-sans mb-10">what you can expect per pod</p>
          <div className="grid grid-cols-3 gap-8 max-w-4xl w-full">
            {[
              { value: "£39,000", label: "annual revenue\nper pod" },
              { value: "4 in 5", label: "of users make an additional purchase on-site" },
              { value: "72%", label: "of users become repeat visitors" },
            ].map((item) => (
              <div key={item.label} className="bg-white text-foreground rounded-3xl p-10 flex flex-col items-center">
                <p className="font-funnel font-bold text-6xl mb-3">{item.value}</p>
                <p className="text-foreground/70 font-sans text-base font-medium whitespace-pre-line">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </Slide>

      {/* Slide 7: Urgency */}
      <Slide>
        <div className="flex flex-col items-center justify-center h-full text-center px-16">
          <h2 className="font-funnel font-bold text-7xl text-foreground leading-[0.9] tracking-tight mb-6">
            we have a limited<br />number of pods<br />
            <span className="font-cormorant italic font-normal">available now.</span>
          </h2>
          <p className="text-xl text-muted-foreground font-sans max-w-2xl leading-relaxed">
            we're selectively partnering with a number of venues for our upcoming rollout.
          </p>
          <p className="text-xl text-muted-foreground font-sans max-w-2xl leading-relaxed mt-3">
            if your space qualifies, we'd love to talk.
          </p>
        </div>
      </Slide>

      {/* Slide 8: Contact */}
      <Slide>
        <div className="flex flex-col items-center justify-center h-full text-center px-16">
          <h2 className="font-funnel font-bold text-8xl text-foreground leading-[0.9] tracking-tight mb-6">qwip</h2>
          <p className="text-xl text-muted-foreground font-sans mb-3 max-w-xl leading-relaxed">
            we're reinventing the public phone booth for the 21st century.
          </p>
          <p className="text-xl text-foreground font-sans font-bold mb-10 max-w-xl leading-relaxed">
            do you want to be a part of it?
          </p>
          <div className="flex flex-col gap-3 items-center">
            <div className="flex items-center gap-3 bg-foreground text-primary-foreground px-8 py-4 rounded-full font-sans font-semibold text-lg">
              <FaWhatsapp className="w-5 h-5" />
              get in touch
            </div>
            <div className="flex items-center gap-3 text-foreground font-sans font-medium text-base">
              <Mail className="w-4 h-4" />
              hello@qwip.co.uk
            </div>
            <div className="flex items-center gap-3 text-foreground font-sans font-medium text-base">
              <Linkedin className="w-4 h-4" />
              <span>founders: Zain &amp; Stiofán</span>
            </div>
          </div>
        </div>
      </Slide>
    </>
  );
};

export default DeckPrint;
